import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { PageLoading } from "@/components/PageLoading";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { RecoveryRedirect } from "@/components/auth/RecoveryRedirect";
import { useToolAnalytics } from "@/hooks/useToolAnalytics";
const ToolMetrics = lazy(() => import("./pages/admin/ToolMetrics"));

// Critical path - loaded immediately
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";

// Lazy loaded routes - code split
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const ProjectCharges = lazy(() => import("./pages/ProjectCharges"));
const ArchivedTopics = lazy(() => import("./pages/ArchivedTopics"));
const Subscription = lazy(() => import("./pages/Subscription"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const Customization = lazy(() => import("./pages/Customization"));
const Profile = lazy(() => import("./pages/Profile"));
const ArchivedProjects = lazy(() => import("./pages/ArchivedProjects"));
const Revenue = lazy(() => import("./pages/Revenue"));
const Clients = lazy(() => import("./pages/Clients"));
const ProfessionalDashboard = lazy(() => import("./pages/ProfessionalDashboard"));
const Updates = lazy(() => import("./pages/Updates"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Schedule = lazy(() => import("./pages/Schedule"));
const ProjectDrawings = lazy(() => import("./pages/ProjectDrawings"));
const ProjectLogs = lazy(() => import("./pages/ProjectLogs"));
const Achievements = lazy(() => import("./pages/Achievements"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const MyTasks = lazy(() => import("./pages/MyTasks"));
const Install = lazy(() => import("./pages/Install"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Forms = lazy(() => import("./pages/Forms"));
const FormBuilder = lazy(() => import("./pages/FormBuilder"));
const FormPublic = lazy(() => import("./pages/FormPublic"));
const ToolsIndex = lazy(() => import("./pages/tools/ToolsIndex"));
const PixQrGenerator = lazy(() => import("./pages/tools/PixQrGenerator"));
const HourlyRateCalculator = lazy(() => import("./pages/tools/HourlyRateCalculator"));
const BudgetGenerator = lazy(() => import("./pages/tools/BudgetGenerator"));
const ReceiptGenerator = lazy(() => import("./pages/tools/ReceiptGenerator"));
const ContractGenerator = lazy(() => import("./pages/tools/ContractGenerator"));
const WhatsappLinkGenerator = lazy(() => import("./pages/tools/WhatsappLinkGenerator"));
const CpfCnpjValidator = lazy(() => import("./pages/tools/CpfCnpjValidator"));
const MeiCalculator = lazy(() => import("./pages/tools/MeiCalculator"));
const BusinessDaysCalculator = lazy(() => import("./pages/tools/BusinessDaysCalculator"));
const PaymentFeeCalculator = lazy(() => import("./pages/tools/PaymentFeeCalculator"));
const SimplesNacionalCalculator = lazy(() => import("./pages/tools/SimplesNacionalCalculator"));
const InssAutonomoCalculator = lazy(() => import("./pages/tools/InssAutonomoCalculator"));
const CarneLeaoCalculator = lazy(() => import("./pages/tools/CarneLeaoCalculator"));
const LateFeeCalculator = lazy(() => import("./pages/tools/LateFeeCalculator"));
const DiscountCalculator = lazy(() => import("./pages/tools/DiscountCalculator"));
const MarginMarkupCalculator = lazy(() => import("./pages/tools/MarginMarkupCalculator"));
const ContractAdjustmentCalculator = lazy(() => import("./pages/tools/ContractAdjustmentCalculator"));
const InstallmentCalculator = lazy(() => import("./pages/tools/InstallmentCalculator"));
const ProjectCostCalculator = lazy(() => import("./pages/tools/ProjectCostCalculator"));
const EmergencyFundCalculator = lazy(() => import("./pages/tools/EmergencyFundCalculator"));
const ProposalGenerator = lazy(() => import("./pages/tools/ProposalGenerator"));
const ServiceDeclarationGenerator = lazy(() => import("./pages/tools/ServiceDeclarationGenerator"));
const NdaGenerator = lazy(() => import("./pages/tools/NdaGenerator"));
const BriefingGenerator = lazy(() => import("./pages/tools/BriefingGenerator"));
const ScheduleGenerator = lazy(() => import("./pages/tools/ScheduleGenerator"));
const EmailSignatureGenerator = lazy(() => import("./pages/tools/EmailSignatureGenerator"));
const PasswordGenerator = lazy(() => import("./pages/tools/PasswordGenerator"));
const QrCodeGenerator = lazy(() => import("./pages/tools/QrCodeGenerator"));
const UtmLinkGenerator = lazy(() => import("./pages/tools/UtmLinkGenerator"));
const SlugGenerator = lazy(() => import("./pages/tools/SlugGenerator"));
const WordCounter = lazy(() => import("./pages/tools/WordCounter"));
const ColorPaletteGenerator = lazy(() => import("./pages/tools/ColorPaletteGenerator"));
const ColorConverter = lazy(() => import("./pages/tools/ColorConverter"));
const LoremIpsumGenerator = lazy(() => import("./pages/tools/LoremIpsumGenerator"));
const MetaTagsGenerator = lazy(() => import("./pages/tools/MetaTagsGenerator"));
const Base64Converter = lazy(() => import("./pages/tools/Base64Converter"));
const JsonFormatter = lazy(() => import("./pages/tools/JsonFormatter"));
const UuidGenerator = lazy(() => import("./pages/tools/UuidGenerator"));
const PercentageCalculator = lazy(() => import("./pages/tools/PercentageCalculator"));
const HoursCalculator = lazy(() => import("./pages/tools/HoursCalculator"));
const AlternativesIndex = lazy(() => import("./pages/compare/AlternativesIndex"));
const AlternativePage = lazy(() => import("./pages/compare/AlternativePage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageTransitionWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return <main className={isVisible ? "page-enter" : "opacity-0"}>{children}</main>;
};

const AppRoutes = () => {
  useToolAnalytics();
  return (
    <PageTransitionWrapper>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/admin/metricas-ferramentas" element={<ToolMetrics />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/project/:projectId" element={<ProjectDetails />} />
          <Route path="/project/:projectId/charges" element={<ProjectCharges />} />
          <Route path="/project/:projectId/archived" element={<ArchivedTopics />} />
          <Route path="/project/:projectId/drawings" element={<ProjectDrawings />} />
          <Route path="/project/:projectId/logs" element={<ProjectLogs />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/customization" element={<Customization />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/archived-projects" element={<ArchivedProjects />} />
          <Route path="/revenue" element={<Revenue />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/professional-dashboard" element={<ProfessionalDashboard />} />
          <Route path="/updates" element={<Updates />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/achievements" element={<Achievements />} />
          <Route path="/client-portal" element={<ClientPortal />} />
          <Route path="/my-tasks" element={<MyTasks />} />
          <Route path="/install" element={<Install />} />
          <Route path="/dashboard/forms" element={<Forms />} />
          <Route path="/dashboard/forms/:formId" element={<FormBuilder />} />
          <Route path="/f/:formId" element={<FormPublic />} />
          <Route path="/ferramentas" element={<ToolsIndex />} />
          <Route path="/ferramentas/gerador-qr-code-pix" element={<PixQrGenerator />} />
          <Route path="/ferramentas/calculadora-preco-hora-freelancer" element={<HourlyRateCalculator />} />
          <Route path="/ferramentas/gerador-de-orcamento" element={<BudgetGenerator />} />
          <Route path="/ferramentas/gerador-de-recibo" element={<ReceiptGenerator />} />
          <Route path="/ferramentas/gerador-de-contrato" element={<ContractGenerator />} />
          <Route path="/ferramentas/gerador-link-whatsapp" element={<WhatsappLinkGenerator />} />
          <Route path="/ferramentas/validador-cpf-cnpj" element={<CpfCnpjValidator />} />
          <Route path="/ferramentas/calculadora-limite-mei" element={<MeiCalculator />} />
          <Route path="/ferramentas/calculadora-dias-uteis" element={<BusinessDaysCalculator />} />
          <Route path="/ferramentas/calculadora-taxa-maquininha" element={<PaymentFeeCalculator />} />
          <Route path="/ferramentas/calculadora-simples-nacional" element={<SimplesNacionalCalculator />} />
          <Route path="/ferramentas/calculadora-inss-autonomo" element={<InssAutonomoCalculator />} />
          <Route path="/ferramentas/calculadora-carne-leao" element={<CarneLeaoCalculator />} />
          <Route path="/ferramentas/calculadora-juros-atraso" element={<LateFeeCalculator />} />
          <Route path="/ferramentas/calculadora-de-desconto" element={<DiscountCalculator />} />
          <Route path="/ferramentas/calculadora-margem-markup" element={<MarginMarkupCalculator />} />
          <Route path="/ferramentas/calculadora-reajuste-contrato" element={<ContractAdjustmentCalculator />} />
          <Route path="/ferramentas/calculadora-de-parcelamento" element={<InstallmentCalculator />} />
          <Route path="/ferramentas/calculadora-custo-de-projeto" element={<ProjectCostCalculator />} />
          <Route path="/ferramentas/calculadora-reserva-de-emergencia" element={<EmergencyFundCalculator />} />
          <Route path="/ferramentas/gerador-de-proposta-comercial" element={<ProposalGenerator />} />
          <Route path="/ferramentas/gerador-de-declaracao-de-servico" element={<ServiceDeclarationGenerator />} />
          <Route path="/ferramentas/gerador-de-nda" element={<NdaGenerator />} />
          <Route path="/ferramentas/gerador-de-briefing" element={<BriefingGenerator />} />
          <Route path="/ferramentas/gerador-de-cronograma" element={<ScheduleGenerator />} />
          <Route path="/ferramentas/gerador-de-assinatura-de-email" element={<EmailSignatureGenerator />} />
          <Route path="/ferramentas/gerador-de-senha" element={<PasswordGenerator />} />
          <Route path="/ferramentas/gerador-de-qr-code" element={<QrCodeGenerator />} />
          <Route path="/ferramentas/gerador-de-link-utm" element={<UtmLinkGenerator />} />
          <Route path="/ferramentas/gerador-de-slug" element={<SlugGenerator />} />
          <Route path="/ferramentas/contador-de-palavras" element={<WordCounter />} />
          <Route path="/ferramentas/gerador-de-paleta-de-cores" element={<ColorPaletteGenerator />} />
          <Route path="/ferramentas/conversor-de-cores" element={<ColorConverter />} />
          <Route path="/ferramentas/gerador-de-lorem-ipsum" element={<LoremIpsumGenerator />} />
          <Route path="/ferramentas/gerador-de-meta-tags" element={<MetaTagsGenerator />} />
          <Route path="/ferramentas/conversor-base64" element={<Base64Converter />} />
          <Route path="/ferramentas/formatador-de-json" element={<JsonFormatter />} />
          <Route path="/ferramentas/gerador-de-uuid" element={<UuidGenerator />} />
          <Route path="/ferramentas/calculadora-de-porcentagem" element={<PercentageCalculator />} />
          <Route path="/ferramentas/calculadora-de-horas" element={<HoursCalculator />} />
          <Route path="/alternativas" element={<AlternativesIndex />} />
          <Route path="/alternativas/:slug" element={<AlternativePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </PageTransitionWrapper>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RecoveryRedirect />
          <AppRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
